<script setup>
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import CheckBox from 'v3/components/Form/CheckBox.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';
import { initializeAudioAlerts } from 'dashboard/helper/scriptHelpers';
import { useStoreGetters } from 'dashboard/composables/store';
import DashboardAudioNotificationHelper from 'dashboard/helper/AudioAlerts/DashboardAudioNotificationHelper';
import {
  areNotificationSoundPreferencesEqual,
  DEFAULT_NOTIFICATION_SOUND_ENABLED,
  DEFAULT_NOTIFICATION_SOUND_PREFERENCES,
  DEFAULT_REPEAT_NEW_MESSAGE_SOUND_UNTIL_READ,
  NOTIFICATION_SOUND_EVENTS,
  sanitizeNotificationSoundPreferences,
} from 'dashboard/helper/AudioAlerts/constants';

const getters = useStoreGetters();
const currentUser = computed(() => getters.getCurrentUser.value);

const { uiSettings, updateUISettings } = useUISettings();

const { t } = useI18n();
const notificationSoundEnabled = ref(DEFAULT_NOTIFICATION_SOUND_ENABLED);
const repeatNewMessageSoundUntilRead = ref(
  DEFAULT_REPEAT_NEW_MESSAGE_SOUND_UNTIL_READ
);
const notificationSoundPreferences = ref({
  ...DEFAULT_NOTIFICATION_SOUND_PREFERENCES,
});

const notificationRows = [
  {
    key: NOTIFICATION_SOUND_EVENTS.NEW_MESSAGE,
    label: 'New message',
  },
  {
    key: NOTIFICATION_SOUND_EVENTS.INCOMING_CHATS,
    label: 'Incoming chats',
  },
  {
    key: NOTIFICATION_SOUND_EVENTS.NEW_TICKETS_TO_HANDLE,
    label: 'New tickets to handle',
  },
  {
    key: NOTIFICATION_SOUND_EVENTS.VISITORS_IN_QUEUE,
    label: 'Visitors in the queue',
  },
  {
    key: NOTIFICATION_SOUND_EVENTS.NEW_VISITORS,
    label: 'New visitors',
  },
  {
    key: NOTIFICATION_SOUND_EVENTS.RETURNING_VISITORS,
    label: 'Returning visitors',
  },
];

const initializeNotificationUISettings = newUISettings => {
  const updatedUISettings = camelcaseKeys(newUISettings || {});
  const rawPreferences = updatedUISettings.notificationSoundPreferences;
  const sanitizedPreferences =
    sanitizeNotificationSoundPreferences(rawPreferences);

  notificationSoundEnabled.value =
    updatedUISettings.notificationSoundEnabled === undefined
      ? DEFAULT_NOTIFICATION_SOUND_ENABLED
      : updatedUISettings.notificationSoundEnabled;
  repeatNewMessageSoundUntilRead.value =
    updatedUISettings.repeatNewMessageSoundUntilRead === undefined
      ? DEFAULT_REPEAT_NEW_MESSAGE_SOUND_UNTIL_READ
      : updatedUISettings.repeatNewMessageSoundUntilRead;
  notificationSoundPreferences.value = sanitizedPreferences;

  const hasRawPreferencesObject =
    rawPreferences &&
    typeof rawPreferences === 'object' &&
    !Array.isArray(rawPreferences);
  const shouldCleanupLegacyPreferences =
    hasRawPreferencesObject &&
    !areNotificationSoundPreferencesEqual(rawPreferences, sanitizedPreferences);

  if (shouldCleanupLegacyPreferences) {
    updateUISettings({
      notification_sound_preferences: sanitizedPreferences,
    });
  }
};

watch(
  uiSettings,
  value => {
    initializeNotificationUISettings(value);
  },
  { immediate: true }
);

const handleAudioConfigChange = value => {
  updateUISettings(value);
  initializeAudioAlerts(currentUser.value);
  useAlert(t('PROFILE_SETTINGS.FORM.API.UPDATE_SUCCESS'));
};

const handleNotificationSoundToggle = value => {
  notificationSoundEnabled.value = value;
  handleAudioConfigChange({
    notification_sound_enabled: value,
  });
};

const handleRepeatSoundToggle = (_id, value) => {
  repeatNewMessageSoundUntilRead.value = value;
  handleAudioConfigChange({
    repeat_new_message_sound_until_read: value,
  });
};

const handleNotificationPreferenceChange = (eventKey, value) => {
  notificationSoundPreferences.value = {
    ...notificationSoundPreferences.value,
    [eventKey]: value,
  };
  handleAudioConfigChange({
    notification_sound_preferences: notificationSoundPreferences.value,
  });
};

const playTestSound = eventKey => {
  DashboardAudioNotificationHelper.playTestSound(eventKey);
};
</script>

<template>
  <div id="profile-settings-notifications" class="flex flex-col gap-4">
    <div
      class="flex items-center justify-between w-full gap-2 p-3 border border-solid border-n-weak rounded-xl"
    >
      <div class="flex items-center gap-2">
        <fluent-icon icon="alert" class="text-n-slate-12" size="18" />
        <span class="text-sm text-n-slate-12">Notification sound</span>
      </div>
      <ToggleSwitch
        v-model="notificationSoundEnabled"
        @change="handleNotificationSoundToggle"
      />
    </div>

    <div class="flex items-start gap-2">
      <CheckBox
        value="repeat-new-message-sound"
        :is-checked="repeatNewMessageSoundUntilRead"
        @update="handleRepeatSoundToggle"
      />
      <label
        for="repeat-new-message-sound"
        class="text-sm font-normal text-n-slate-12"
      >
        Repeat <strong>New message</strong> sound until message is read
      </label>
    </div>

    <div>
      <label class="text-sm font-medium text-n-slate-12">Notify me about...</label>
      <div class="mt-3 divide-y divide-n-weak border border-n-weak rounded-xl">
        <div
          v-for="item in notificationRows"
          :key="item.key"
          class="flex items-center justify-between gap-3 px-3 py-3"
        >
          <div class="flex items-center gap-2 min-w-0">
            <CheckBox
              :value="`notification-sound-${item.key}`"
              :is-checked="notificationSoundPreferences[item.key]"
              @update="
                (_id, value) =>
                  handleNotificationPreferenceChange(item.key, value)
              "
            />
            <span class="text-sm text-n-slate-12 truncate">
              {{ item.label }}
            </span>
            <button
              type="button"
              class="text-sm text-n-brand hover:underline"
              @click="playTestSound(item.key)"
            >
              (test)
            </button>
          </div>
          <span class="text-sm font-medium text-n-slate-12">Default sound</span>
        </div>
      </div>
    </div>
  </div>
</template>
